import { Configuration } from "../configuration";
import { resolveHostOrigin } from "./origin";
import {
    GrantReason,
    CoinGrantStatus,
    CoinRejectionCode,
    MessageType,
    messageTypeOf,
    parseGameOver,
    parseGrantCoins,
    parseLevelComplete,
    isExpectedOrigin,
} from "./contract";
import { HostMessage } from "./messages";
import { BridgeError, BridgeTimeoutError, BridgeValidationError, CoinGrantRejectedError, TokenRefreshError } from "./errors";

const DEBUG_FLAG = "gj_postmsg_devtools";
const SECRET_KEYS = new Set(["authToken", "refreshToken", "accessToken", "token"]);
const DEFAULT_IDENTITY_TIMEOUT_MS = 2000;
const DEFAULT_REQUEST_TIMEOUT_MS = 10000;
const DEFAULT_RETRY_DELAYS_MS = [100, 300, 700];

export interface Identity {
    status: "authenticated" | "guest";
    authToken: string | null;
    refreshToken: string | null;
    anonymousId: string | null;
    eightpointId: string | null;
}

export type BridgeEvent = "identity" | "logout" | "tokenRefreshed" | "tokenRefreshFailed" | "coinsGranted" | "coinsRejected";

export interface GameOverAck {
    roundId: string;
}

export interface LevelCompleteAck {
    roundId: string;
    level: number | string;
}

export interface CoinsGranted {
    reason: string;
    amount: number;
    newBalance: number;
    status: CoinGrantStatus;
    replayed: boolean;
}

export interface TokenRefreshed {
    authToken: string;
    refreshToken: string | null;
}

export interface DebugEvent {
    direction: "send" | "recv" | "drop";
    type?: string;
    reason?: string;
    data?: unknown;
}

export interface BridgeStats {
    sends: Record<string, number>;
    receives: Record<string, number>;
    drops: Record<string, number>;
    timeouts: Record<string, number>;
    pendingCount: number;
    gameLoadedRetries: number;
}

export interface ConnectOptions {
    configuration?: Configuration;
    hostOrigin?: string;
    identityTimeoutMs?: number;
    requestTimeoutMs?: number;
    retryDelaysMs?: number[];
    protocolVersion?: string;
    onDebug?: (event: DebugEvent) => void;
}

export interface GetjarHost {
    readonly identity: Identity;
    gameStarted(input: { roundId: string }): void;
    gameOver(input: { roundId: string; score: number; durationMs: number; metadata?: Record<string, unknown> }): Promise<GameOverAck>;
    levelComplete(input: {
        roundId: string;
        level: number | string;
        score?: number;
        durationMs?: number;
        metadata?: Record<string, unknown>;
    }): Promise<LevelCompleteAck>;
    grantCoins(input: { reason: GrantReason; amount: number; eventId?: string }): Promise<CoinsGranted>;
    refreshToken(): Promise<TokenRefreshed>;
    redirectToLogin(): void;
    updateBalance(input: { currency: number }): void;
    debugLog(message: unknown): void;
    errorLog(context: string, message: string): void;
    on(event: BridgeEvent, handler: (payload: any) => void): () => void;
    onMessage<T extends HostMessage["type"]>(type: T, handler: (msg: Extract<HostMessage, { type: T }>) => void): () => void;
    disconnect(): void;
    getStats(): BridgeStats;
}

interface PendingRequest {
    resolve: (value: any) => void;
    reject: (error: Error) => void;
    timeoutHandle: ReturnType<typeof setTimeout>;
    type: string;
}

function redact(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
    if (!value || typeof value !== "object") return value;
    if (seen.has(value as object)) return "[circular]";
    seen.add(value as object);
    try {
        if (Array.isArray(value)) return value.map((item) => redact(item, seen));
        const out: Record<string, unknown> = {};
        for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
            out[key] = SECRET_KEYS.has(key) ? (item ? "[redacted]" : item) : redact(item, seen);
        }
        return out;
    } finally {
        seen.delete(value as object);
    }
}

function postmsgDebugEnabled(): boolean {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(DEBUG_FLAG) === "1";
    } catch {
        return false;
    }
}

function gameOverKey(roundId: string): string {
    return `GAME_OVER:${roundId}`;
}

function levelCompleteKey(roundId: string, level: number | string): string {
    return `LEVEL_COMPLETE:${roundId}|${level}`;
}

function grantCoinsKey(reason: GrantReason): string {
    return `GRANT_COINS:${reason}`;
}

class GameBridge implements GetjarHost {
    private readonly configuration?: Configuration;
    private readonly hostOrigin: string;
    private readonly requestTimeoutMs: number;
    private readonly protocolVersion?: string;
    private readonly onDebug?: (event: DebugEvent) => void;

    private readonly pending = new Map<string, PendingRequest>();
    private readonly grantQueues = new Map<GrantReason, Promise<void>>();
    private readonly listeners = new Map<BridgeEvent, Set<(payload: any) => void>>();
    private readonly messageListeners = new Map<MessageType, Set<(msg: any) => void>>();

    private refreshInFlight: Promise<TokenRefreshed> | null = null;
    private refreshResolvers: { resolve: (v: TokenRefreshed) => void; reject: (e: Error) => void; timeoutHandle: ReturnType<typeof setTimeout> } | null = null;

    private currentIdentity: Identity;
    private onFirstIdentity: ((identity: Identity) => void) | null = null;
    private eventIdCounter = 0;
    private disconnected = false;

    private readonly stats: BridgeStats = {
        sends: {},
        receives: {},
        drops: {},
        timeouts: {},
        pendingCount: 0,
        gameLoadedRetries: 0,
    };

    private readonly windowMessageHandler = (event: MessageEvent) => this.handleInbound(event);

    constructor(hostOrigin: string, configuration: Configuration | undefined, requestTimeoutMs: number, protocolVersion: string | undefined, onDebug: ((event: DebugEvent) => void) | undefined) {
        this.hostOrigin = hostOrigin;
        this.configuration = configuration;
        this.requestTimeoutMs = requestTimeoutMs;
        this.protocolVersion = protocolVersion;
        this.onDebug = onDebug;
        this.currentIdentity = { status: "guest", authToken: null, refreshToken: null, anonymousId: null, eightpointId: null };
        window.addEventListener("message", this.windowMessageHandler);
    }

    get identity(): Identity {
        return this.currentIdentity;
    }

    handshake(identityTimeoutMs: number, retryDelaysMs: number[]): Promise<Identity> {
        return new Promise((resolve) => {
            let resolved = false;
            const timers: ReturnType<typeof setTimeout>[] = [];

            const finish = (identity: Identity) => {
                if (resolved) return;
                resolved = true;
                timers.forEach(clearTimeout);
                resolve(identity);
            };
            this.onFirstIdentity = finish;

            this.send({ type: "GAME_LOADED" });

            for (const delay of retryDelaysMs) {
                timers.push(
                    setTimeout(() => {
                        if (resolved) return;
                        this.stats.gameLoadedRetries += 1;
                        this.send({ type: "GAME_LOADED" });
                    }, delay)
                );
            }

            timers.push(
                setTimeout(() => {
                    if (resolved) return;
                    const guestIdentity: Identity = { status: "guest", authToken: null, refreshToken: null, anonymousId: null, eightpointId: null };
                    this.applyIdentity(guestIdentity);
                    finish(guestIdentity);
                }, identityTimeoutMs)
            );
        });
    }

    gameStarted(input: { roundId: string }): void {
        this.send({ type: "GAME_STARTED", roundId: input.roundId });
    }

    gameOver(input: { roundId: string; score: number; durationMs: number; metadata?: Record<string, unknown> }): Promise<GameOverAck> {
        const fields = parseGameOver(input);
        if (!fields) {
            return Promise.reject(
                new BridgeValidationError(
                    "GAME_OVER requires a non-empty roundId and finite score and durationMs. The host drops an invalid GAME_OVER without an ack."
                )
            );
        }
        const key = gameOverKey(fields.roundId);
        return new Promise((resolve, reject) => {
            this.registerPending(key, "GAME_OVER", resolve, reject);
            const delivered = this.send({
                type: "GAME_OVER",
                roundId: fields.roundId,
                score: fields.score,
                durationMs: fields.durationMs,
                metadata: fields.metadata,
            });
            if (!delivered) this.rejectPending(key, this.undeliveredError("GAME_OVER"));
        });
    }

    levelComplete(input: {
        roundId: string;
        level: number | string;
        score?: number;
        durationMs?: number;
        metadata?: Record<string, unknown>;
    }): Promise<LevelCompleteAck> {
        const fields = parseLevelComplete(input);
        if (!fields) {
            return Promise.reject(
                new BridgeValidationError(
                    "LEVEL_COMPLETE requires a non-empty roundId and a level that is a number or a string. The host drops an invalid LEVEL_COMPLETE without an ack."
                )
            );
        }
        const key = levelCompleteKey(fields.roundId, fields.level);
        return new Promise((resolve, reject) => {
            this.registerPending(key, "LEVEL_COMPLETE", resolve, reject);
            const delivered = this.send({
                type: "LEVEL_COMPLETE",
                roundId: fields.roundId,
                level: fields.level,
                score: fields.score,
                durationMs: fields.durationMs,
                metadata: fields.metadata,
            });
            if (!delivered) this.rejectPending(key, this.undeliveredError("LEVEL_COMPLETE"));
        });
    }

    grantCoins(input: { reason: GrantReason; amount: number; eventId?: string }): Promise<CoinsGranted> {
        if (!parseGrantCoins(input)) {
            return Promise.reject(
                new BridgeValidationError(
                    "GRANT_COINS requires amount to be a finite number greater than zero and reason to be one of LEVEL_COMPLETE, ACHIEVEMENT, DAILY_OBJECTIVE, IDLE_REWARD (case sensitive). The host rejects anything else as INVALID."
                )
            );
        }
        const reason = input.reason;
        const prior: Promise<void> = this.grantQueues.get(reason) ?? Promise.resolve();
        const task: Promise<CoinsGranted> = prior.then(
            () => this.sendGrantCoins(input),
            () => this.sendGrantCoins(input)
        );
        const settled: Promise<void> = task.then(
            (): void => undefined,
            (): void => undefined
        );
        this.grantQueues.set(reason, settled);
        return task;
    }

    private sendGrantCoins(input: { reason: GrantReason; amount: number; eventId?: string }): Promise<CoinsGranted> {
        const key = grantCoinsKey(input.reason);
        const eventId = input.eventId ?? this.mintEventId();
        return new Promise((resolve, reject) => {
            this.registerPending(key, "GRANT_COINS", resolve, reject);
            const delivered = this.send({ type: "GRANT_COINS", reason: input.reason, amount: input.amount, eventId });
            if (!delivered) this.rejectPending(key, this.undeliveredError("GRANT_COINS"));
        });
    }

    refreshToken(): Promise<TokenRefreshed> {
        if (this.disconnected) return Promise.reject(this.disconnectedError("REFRESH_TOKEN"));
        if (this.refreshInFlight) return this.refreshInFlight;

        const promise = new Promise<TokenRefreshed>((resolve, reject) => {
            const timeoutHandle = setTimeout(() => {
                this.stats.timeouts["REFRESH_TOKEN"] = (this.stats.timeouts["REFRESH_TOKEN"] ?? 0) + 1;
                this.refreshInFlight = null;
                this.refreshResolvers = null;
                reject(new BridgeTimeoutError("REFRESH_TOKEN timed out waiting for a reply from the host."));
            }, this.requestTimeoutMs);
            this.refreshResolvers = { resolve, reject, timeoutHandle };
        });

        this.refreshInFlight = promise;
        if (!this.send({ type: "REFRESH_TOKEN" })) {
            this.settleRefreshFailure(this.undeliveredError("REFRESH_TOKEN"));
        }
        return promise;
    }

    redirectToLogin(): void {
        this.send({ type: "REDIRECT_TO_LOGIN" });
    }

    updateBalance(input: { currency: number }): void {
        this.send({ type: "CHILD_DATA_UPDATE", payload: { currency: input.currency } });
    }

    debugLog(message: unknown): void {
        this.send({ type: "DEBUG_LOG", payload: { timestamp: new Date().toISOString(), message } });
    }

    errorLog(context: string, message: string): void {
        this.send({ type: "ERROR_LOG", payload: { context, message, timestamp: new Date().toISOString() } });
    }

    on(event: BridgeEvent, handler: (payload: any) => void): () => void {
        let set = this.listeners.get(event);
        if (!set) {
            set = new Set();
            this.listeners.set(event, set);
        }
        set.add(handler);
        return () => set?.delete(handler);
    }

    onMessage<T extends HostMessage["type"]>(type: T, handler: (msg: Extract<HostMessage, { type: T }>) => void): () => void {
        let set = this.messageListeners.get(type);
        if (!set) {
            set = new Set();
            this.messageListeners.set(type, set);
        }
        set.add(handler as (msg: any) => void);
        return () => set?.delete(handler as (msg: any) => void);
    }

    disconnect(): void {
        if (this.disconnected) return;
        this.disconnected = true;
        window.removeEventListener("message", this.windowMessageHandler);
        for (const [key, entry] of Array.from(this.pending.entries())) {
            this.rejectPending(key, this.disconnectedError(entry.type));
        }
        this.settleRefreshFailure(this.disconnectedError("REFRESH_TOKEN"));
        this.grantQueues.clear();
        this.listeners.clear();
        this.messageListeners.clear();
    }

    getStats(): BridgeStats {
        return {
            sends: { ...this.stats.sends },
            receives: { ...this.stats.receives },
            drops: { ...this.stats.drops },
            timeouts: { ...this.stats.timeouts },
            pendingCount: this.pending.size,
            gameLoadedRetries: this.stats.gameLoadedRetries,
        };
    }

    private undeliveredError(type: string): BridgeError {
        return new BridgeError(`${type} was not delivered: the game is not running inside an iframe.`);
    }

    private disconnectedError(type: string): BridgeError {
        return new BridgeError(`${type} was abandoned: the bridge was disconnected.`);
    }

    private settleRefreshFailure(error: Error): void {
        if (this.refreshResolvers) {
            clearTimeout(this.refreshResolvers.timeoutHandle);
            this.refreshResolvers.reject(error);
            this.refreshResolvers = null;
        }
        this.refreshInFlight = null;
    }

    private downgradeToGuest(): void {
        this.currentIdentity = { ...this.currentIdentity, status: "guest", authToken: null, refreshToken: null };
        this.configuration?.setAccessToken(undefined);
        this.emit("identity", this.currentIdentity);
    }

    private mintEventId(): string {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
        this.eventIdCounter += 1;
        return `${Date.now()}-${this.eventIdCounter}`;
    }

    private registerPending(key: string, type: string, resolve: (value: any) => void, reject: (error: Error) => void): void {
        if (this.disconnected) {
            reject(this.disconnectedError(type));
            return;
        }
        const timeoutHandle = setTimeout(() => {
            this.pending.delete(key);
            this.stats.timeouts[type] = (this.stats.timeouts[type] ?? 0) + 1;
            reject(new BridgeTimeoutError(`${type} timed out waiting for a reply from the host.`));
        }, this.requestTimeoutMs);
        this.pending.set(key, { resolve, reject, timeoutHandle, type });
    }

    private resolvePending(key: string, value: any): boolean {
        const entry = this.pending.get(key);
        if (!entry) return false;
        this.pending.delete(key);
        clearTimeout(entry.timeoutHandle);
        entry.resolve(value);
        return true;
    }

    private rejectPending(key: string, error: Error): boolean {
        const entry = this.pending.get(key);
        if (!entry) return false;
        this.pending.delete(key);
        clearTimeout(entry.timeoutHandle);
        entry.reject(error);
        return true;
    }

    private emit(event: BridgeEvent, payload: any): void {
        const set = this.listeners.get(event);
        if (!set) return;
        set.forEach((handler) => handler(payload));
    }

    private dispatchMessage(type: MessageType, msg: any): void {
        const set = this.messageListeners.get(type);
        if (!set) return;
        set.forEach((handler) => handler(msg));
    }

    private applyIdentity(identity: Identity): void {
        this.currentIdentity = identity;
        if (this.configuration) {
            const userId = identity.eightpointId ?? identity.anonymousId;
            if (userId) this.configuration.setUserId(userId);
            if (identity.authToken) this.configuration.setAccessToken(identity.authToken);
        }
        this.emit("identity", identity);
    }

    private send(message: Record<string, unknown> & { type: string }): boolean {
        const outbound = this.protocolVersion ? { ...message, protocolVersion: this.protocolVersion } : message;

        if (this.disconnected) {
            this.trace("drop", message.type, "bridge disconnected", outbound);
            return false;
        }

        if (typeof window === "undefined" || window.parent === window) {
            this.trace("drop", message.type, "not running inside an iframe", outbound);
            return false;
        }

        window.parent.postMessage(outbound, this.hostOrigin);
        this.stats.sends[message.type] = (this.stats.sends[message.type] ?? 0) + 1;
        this.trace("send", message.type, undefined, outbound);
        return true;
    }

    private handleInbound(event: MessageEvent): void {
        const rawType = typeof (event.data as { type?: unknown } | null)?.type === "string" ? (event.data as { type: string }).type : undefined;

        if (event.source !== window.parent) {
            if (rawType) this.trace("drop", rawType, "wrong source", { origin: event.origin });
            return;
        }
        if (!isExpectedOrigin(event.origin, this.hostOrigin)) {
            if (rawType) this.trace("drop", rawType, "origin mismatch", { got: event.origin, expected: this.hostOrigin });
            return;
        }

        const type = messageTypeOf(event.data);
        if (!type) {
            this.trace("drop", rawType, "unknown or missing type", event.data);
            return;
        }

        this.stats.receives[type] = (this.stats.receives[type] ?? 0) + 1;
        this.trace("recv", type, undefined, event.data);

        const data = event.data as Record<string, unknown>;
        const payload = (data.payload as Record<string, unknown> | undefined) ?? data;

        switch (type) {
            case "PARENT_DATA": {
                const authToken = typeof payload.authToken === "string" && payload.authToken.length > 0 ? payload.authToken : null;
                const refreshToken = payload.refreshToken === "" ? null : (payload.refreshToken as string | null) ?? null;
                const anonymousId = (payload.anonymousId as string | null) ?? null;
                const eightpointId = (payload.eightpointId as string | null) ?? null;
                const identity: Identity = { status: authToken ? "authenticated" : "guest", authToken, refreshToken, anonymousId, eightpointId };
                this.applyIdentity(identity);
                this.onFirstIdentity?.(identity);
                this.dispatchMessage(type, { type, payload: { authToken, refreshToken, anonymousId, eightpointId } });
                break;
            }
            case "ANON_ID": {
                const anonymousId = payload.anonymousId as string;
                const eightpointId = (payload.eightpointId as string | null) ?? null;
                const identity: Identity = { status: "guest", authToken: null, refreshToken: null, anonymousId, eightpointId };
                this.applyIdentity(identity);
                this.onFirstIdentity?.(identity);
                this.dispatchMessage(type, { type, payload: { anonymousId, eightpointId } });
                break;
            }
            case "USER_LOGGEDOUT": {
                const message = payload.message as string;
                this.downgradeToGuest();
                this.emit("logout", { message });
                this.dispatchMessage(type, { type, payload: { message } });
                break;
            }
            case "TOKEN_REFRESHED": {
                const authToken = payload.authToken as string;
                const refreshToken = payload.refreshToken === "" ? null : (payload.refreshToken as string | null) ?? null;
                this.currentIdentity = {
                    ...this.currentIdentity,
                    status: authToken ? "authenticated" : this.currentIdentity.status,
                    authToken,
                    refreshToken,
                };
                if (this.configuration) this.configuration.setAccessToken(authToken);
                if (this.refreshResolvers) {
                    clearTimeout(this.refreshResolvers.timeoutHandle);
                    this.refreshResolvers.resolve({ authToken, refreshToken });
                    this.refreshResolvers = null;
                    this.refreshInFlight = null;
                }
                this.emit("tokenRefreshed", { authToken, refreshToken });
                this.dispatchMessage(type, { type, payload: { authToken, refreshToken, success: true } });
                break;
            }
            case "TOKEN_REFRESH_FAILED": {
                const error = payload.error as string;
                this.downgradeToGuest();
                this.settleRefreshFailure(new TokenRefreshError(error));
                this.emit("tokenRefreshFailed", { error });
                this.dispatchMessage(type, { type, payload: { success: false, error } });
                break;
            }
            case "GAME_OVER_ACK": {
                const roundId = data.roundId as string;
                this.resolvePending(gameOverKey(roundId), { roundId });
                this.dispatchMessage(type, { type, roundId });
                break;
            }
            case "LEVEL_COMPLETE_ACK": {
                const roundId = data.roundId as string;
                const level = data.level as number | string;
                this.resolvePending(levelCompleteKey(roundId, level), { roundId, level });
                this.dispatchMessage(type, { type, roundId, level });
                break;
            }
            case "COINS_GRANTED": {
                const reason = data.reason as string;
                const value: CoinsGranted = {
                    reason,
                    amount: data.amount as number,
                    newBalance: data.newBalance as number,
                    status: data.status as CoinGrantStatus,
                    replayed: data.replayed as boolean,
                };
                this.resolvePending(grantCoinsKey(reason as GrantReason), value);
                this.emit("coinsGranted", value);
                this.dispatchMessage(type, { type, ...value });
                break;
            }
            case "COINS_REJECTED": {
                const reason = data.reason as string;
                const code = data.code as CoinRejectionCode;
                const retryable = data.retryable as boolean;
                this.rejectPending(grantCoinsKey(reason as GrantReason), new CoinGrantRejectedError(`Coin grant rejected: ${code}`, code, retryable));
                this.emit("coinsRejected", { reason, code, retryable });
                this.dispatchMessage(type, { type, reason, code, retryable });
                break;
            }
            default:
                this.dispatchMessage(type, data);
                break;
        }
    }

    private trace(direction: "send" | "recv" | "drop", type: string | undefined, reason: string | undefined, data: unknown): void {
        if (direction === "drop") {
            this.stats.drops[reason ?? "unknown"] = (this.stats.drops[reason ?? "unknown"] ?? 0) + 1;
        }
        const debugEvent: DebugEvent = { direction, type, reason, data: redact(data) };
        this.onDebug?.(debugEvent);
        if (!postmsgDebugEnabled()) return;
        const prefix = "[postmsg:" + direction + "]";
        if (direction === "drop") {
            console.warn(prefix, type, reason, redact(data));
        } else if (direction === "recv") {
            console.log(prefix, "recv ←", type, redact(data));
        } else {
            console.log(prefix, type, redact(data));
        }
    }
}

export function connect(options: ConnectOptions = {}): Promise<GetjarHost> {
    const hostOrigin = resolveHostOrigin(options.hostOrigin);
    const identityTimeoutMs = options.identityTimeoutMs ?? DEFAULT_IDENTITY_TIMEOUT_MS;
    const requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    const retryDelaysMs = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;

    const bridge = new GameBridge(hostOrigin, options.configuration, requestTimeoutMs, options.protocolVersion, options.onDebug);

    return bridge.handshake(identityTimeoutMs, retryDelaysMs).then(() => bridge);
}
